/**
 * useMCQQuiz Hook
 * Manages state for student MCQ quiz taking experience
 */

import {
  useState, useMemo, useCallback, useEffect, useRef,
} from 'react';
import {
  useGetMcqStudentQuestions,
  usePersistMcqAnswer,
  useSubmitMcqAttempt,
} from '@/common/api/endpoints/mcq-submissions/mcq-submissions';
import { useGetExamSessionStatus } from '@/common/api/endpoints/exam-sessions/exam-sessions';
import type {
  MCQAttemptResponse,
  MCQResultSummary,
  SubmissionMcqResult,
} from '@/common/api/models';
import type { MCQQuestion } from '../types';
import { useMcqAttemptStore } from '../store/useMcqAttemptStore';

type MCQError = {
  message: string;
  code?: string;
};

type MCQQuizState =
  | { status: 'loading' }
  | { status: 'error'; error: MCQError; retry: () => void }
  | { status: 'cold_replay_failed'; error: MCQError; retryColdReplay: () => void }
  | { status: 'needs_booking' }
  | { status: 'ready'; questions: MCQQuestion[]; answers: Record<string, string>; examStatus?: ExamStatus | null }
  | { status: 'submitting'; examStatus?: ExamStatus | null }
  | { status: 'submitted'; results: MCQAttemptResponse }
  | { status: 'waiting_for_exam'; examStatus: ExamStatus | null; pollExamStatus: () => void }
  | { status: 'waiting_for_admission'; examStatus: ExamStatus | null; pollExamStatus: () => void }
  | { status: 'exam_closed'; examStatus: ExamStatus | null };

type ExamStatus = {
  isOpen: boolean;
  openedAt: string | null;
  closedAt: string | null;
  autoCloseAt: string | null;
  timeRemainingSeconds?: number | null;
};

interface UseMCQQuizParams {
  courseId: string;
  moduleId: string;
  taskId: string;
  version?: string;
  slotId?: string;
  submissionMcqResult?: SubmissionMcqResult | null;
  showImmediateFeedback?: boolean;
  readOnly?: boolean;
}

type UseMCQQuizReturn = MCQQuizState & {
  selectAnswer: (questionId: string, optionId: string) => void;
  submit: (options?: { force?: boolean; flush?: boolean }) => Promise<void>;
  canSubmit: () => boolean;
  getAnsweredCount: () => number;
  resetForRetry: () => Promise<void>;
  examStatus?: ExamStatus | null;
  pollExamStatus?: () => void;
};

type HydratableQuestion = MCQQuestion & { selectedOptionId?: string | null };

function hasDraftSelectedOptions(questions: HydratableQuestion[] | undefined): boolean {
  return Boolean(questions?.some((question) => question.selectedOptionId));
}

function isScoredSubmissionMcqResult(
  mcqResult: SubmissionMcqResult | null | undefined,
): mcqResult is MCQResultSummary {
  if (!mcqResult) return false;
  // Terminal score only — attempts>0 alone is the draft-only from_db shape and must not cold-replay.
  return mcqResult.score != null || mcqResult.maxScore != null;
}

export function useMCQQuiz(params: UseMCQQuizParams): UseMCQQuizReturn {
  const {
    courseId,
    moduleId,
    taskId,
    version = 'latest',
    readOnly = false,
    slotId,
    submissionMcqResult,
  } = params;
  const attemptKey = useMemo(
    () => `${courseId}:${moduleId}:${taskId}:${version}`,
    [courseId, moduleId, taskId, version],
  );
  const answers = useMcqAttemptStore((state) => state.answers);
  const setAttemptKey = useMcqAttemptStore((state) => state.setAttemptKey);
  const hydrateFromServer = useMcqAttemptStore((state) => state.hydrateFromServer);
  const selectAttemptAnswer = useMcqAttemptStore((state) => state.selectAnswer);
  const resetAttempt = useMcqAttemptStore((state) => state.resetAttempt);
  const recordScoredResults = useMcqAttemptStore((state) => state.recordScoredResults);
  const clearScoredResults = useMcqAttemptStore((state) => state.clearScoredResults);
  const markRetryCleared = useMcqAttemptStore((state) => state.markRetryCleared);
  const clearRetryCleared = useMcqAttemptStore((state) => state.clearRetryCleared);
  const retryCleared = useMcqAttemptStore(
    (state) => Boolean(state.retryClearedByAttemptKey[attemptKey]),
  );
  const persistedScoredResults = useMcqAttemptStore(
    (state) => state.scoredResultsByAttemptKey[attemptKey] ?? null,
  );
  const [submittedResults, setSubmittedResults] = useState<MCQAttemptResponse | null>(null);
  const [coldReplayFailed, setColdReplayFailed] = useState(false);
  const [coldReplayError, setColdReplayError] = useState<MCQError | null>(null);
  const coldReplayStartedRef = useRef(false);
  const requiresExamSession = Boolean(slotId);
  const persistTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const examStatusQuery = useGetExamSessionStatus(slotId || '', {
    query: {
      enabled: requiresExamSession,
      refetchInterval: requiresExamSession ? 10000 : false,
    },
  });
  const refetchExamStatus = examStatusQuery.refetch;

  const examStatus = useMemo<ExamStatus | null>(() => {
    if (!requiresExamSession) return null;
    const session = examStatusQuery.data?.data?.data;
    if (!session) {
      return {
        isOpen: false,
        openedAt: null,
        closedAt: null,
        autoCloseAt: null,
        timeRemainingSeconds: null,
      };
    }
    const openedAt = session.openedAt ?? null;
    const closedAt = session.closedAt ?? null;
    const autoCloseAt = session.autoCloseAt ?? null;
    return {
      isOpen: Boolean(openedAt) && !closedAt,
      openedAt,
      closedAt,
      autoCloseAt,
      timeRemainingSeconds: session.timeRemainingSeconds ?? null,
    };
  }, [requiresExamSession, examStatusQuery.data]);

  // Fetch questions
  const {
    data: questionsData,
    isLoading,
    isError,
    error,
    refetch,
  } = useGetMcqStudentQuestions(
    courseId,
    moduleId,
    taskId,
    { version },
    {
      query: {
        enabled: !requiresExamSession || Boolean(examStatus?.isOpen),
      },
    },
  );

  const pollExamStatus = useCallback(async () => {
    if (!requiresExamSession) return;
    const examResult = await refetchExamStatus();
    const session = examResult.data?.data?.data;
    const isOpen = Boolean(session?.openedAt) && !session?.closedAt;
    if (!isOpen) return;
    if (isError || !questionsData?.data) {
      await refetch();
    }
  }, [requiresExamSession, refetchExamStatus, isError, questionsData, refetch]);

  // Submit mutation
  const submitMutation = useSubmitMcqAttempt();
  const persistAnswerMutation = usePersistMcqAnswer();

  useEffect(() => {
    setAttemptKey(attemptKey);
  }, [attemptKey, setAttemptKey]);

  useEffect(() => {
    if (!submissionMcqResult) {
      clearRetryCleared(attemptKey);
      coldReplayStartedRef.current = false;
      setColdReplayFailed(false);
      setColdReplayError(null);
    }
  }, [submissionMcqResult, attemptKey, clearRetryCleared]);

  useEffect(() => {
    const fetchedQuestions = questionsData?.data as HydratableQuestion[] | undefined;
    if (!fetchedQuestions?.length) return;

    const serverAnswers = fetchedQuestions.reduce<Record<string, string>>((acc, question) => {
      if (question.selectedOptionId) {
        acc[question.id] = question.selectedOptionId;
      }
      return acc;
    }, {});
    hydrateFromServer(serverAnswers);
  }, [questionsData?.data, hydrateFromServer]);

  useEffect(() => () => {
    Object.values(persistTimersRef.current).forEach((timer) => clearTimeout(timer));
    persistTimersRef.current = {};
  }, []);

  // Select answer callback
  const selectAnswer = useCallback((questionId: string, optionId: string) => {
    if (readOnly) return;
    selectAttemptAnswer(questionId, optionId);

    if (!requiresExamSession) return;

    const existingTimer = persistTimersRef.current[questionId];
    if (existingTimer) clearTimeout(existingTimer);

    persistTimersRef.current[questionId] = setTimeout(() => {
      persistAnswerMutation.mutate({
        courseId,
        moduleId,
        taskId,
        data: { questionId, optionId },
        params: { version },
      });
      delete persistTimersRef.current[questionId];
    }, 250);
  }, [courseId, moduleId, taskId, version, requiresExamSession, persistAnswerMutation, selectAttemptAnswer, readOnly]);

  // Get answered count
  const getAnsweredCount = useCallback(() => Object.keys(answers).length, [answers]);

  // Can submit check
  const canSubmit = useCallback(() => {
    if (!questionsData?.data) return false;
    const questions = questionsData.data as MCQQuestion[];
    return questions.length > 0 && questions.every((q) => answers[q.id]);
  }, [questionsData, answers]);

  const flushPendingPersists = useCallback(() => {
    if (readOnly) return;
    const pendingQuestionIds = Object.keys(persistTimersRef.current);
    pendingQuestionIds.forEach((questionId) => {
      clearTimeout(persistTimersRef.current[questionId]);
      delete persistTimersRef.current[questionId];
    });

    if (!requiresExamSession || pendingQuestionIds.length === 0) return;

    const currentAnswers = useMcqAttemptStore.getState().answers;
    pendingQuestionIds.forEach((questionId) => {
      const optionId = currentAnswers[questionId];
      if (!optionId) return;
      persistAnswerMutation.mutate({
        courseId,
        moduleId,
        taskId,
        data: { questionId, optionId },
        params: { version },
      });
    });
  }, [courseId, moduleId, taskId, version, requiresExamSession, persistAnswerMutation, readOnly]);

  const shouldColdReplay = useMemo(() => {
    if (retryCleared) return false;
    if (submittedResults || persistedScoredResults) return false;
    if (!isScoredSubmissionMcqResult(submissionMcqResult)) return false;

    const fetchedQuestions = questionsData?.data as HydratableQuestion[] | undefined;
    const examIsClosed = requiresExamSession && Boolean(examStatus?.closedAt);

    if (fetchedQuestions?.length) {
      if (hasDraftSelectedOptions(fetchedQuestions)) return false;
    } else if (!examIsClosed) {
      // Exam still open (or practice): wait for questions so draft selections can block replay.
      return false;
    }

    return true;
  }, [
    retryCleared,
    submittedResults,
    persistedScoredResults,
    questionsData?.data,
    submissionMcqResult,
    requiresExamSession,
    examStatus?.closedAt,
  ]);

  useEffect(() => {
    if (
      !shouldColdReplay
      || coldReplayFailed
      || coldReplayStartedRef.current
      || submitMutation.isPending
    ) {
      return;
    }

    coldReplayStartedRef.current = true;

    submitMutation.mutateAsync({
      courseId,
      moduleId,
      taskId,
      data: { answers: {} },
      params: { version },
    }).then((result) => {
      setColdReplayFailed(false);
      setColdReplayError(null);
      setSubmittedResults(result.data);
      recordScoredResults(attemptKey, result.data);
      resetAttempt();
    }).catch((error: unknown) => {
      coldReplayStartedRef.current = false;
      const errorWithResponse = error as { message?: string; code?: string };
      setColdReplayFailed(true);
      setColdReplayError({
        message: errorWithResponse?.message || 'Network request failed',
        code: errorWithResponse?.code,
      });
    });
  }, [
    shouldColdReplay,
    coldReplayFailed,
    submitMutation,
    courseId,
    moduleId,
    taskId,
    version,
    attemptKey,
    recordScoredResults,
    resetAttempt,
  ]);

  const resolvedSubmittedResults = submittedResults ?? persistedScoredResults;

  // Submit callback
  const submit = useCallback(async (options?: { force?: boolean; flush?: boolean }) => {
    if (options?.flush) {
      flushPendingPersists();
    }

    const currentAnswers = useMcqAttemptStore.getState().answers;
    const hasPriorSuccessfulSubmit = Boolean(
      submittedResults
      || persistedScoredResults
      || useMcqAttemptStore.getState().scoredResultsByAttemptKey[attemptKey],
    );

    if (options?.force) {
      if (hasPriorSuccessfulSubmit) return;
      if (shouldColdReplay || coldReplayFailed) return;
      if (Object.keys(currentAnswers).length === 0) return;
    } else if (!canSubmit()) {
      return;
    }

    try {
      const result = await submitMutation.mutateAsync({
        courseId,
        moduleId,
        taskId,
        data: { answers: currentAnswers },
        params: { version },
      });
      setSubmittedResults(result.data);
      recordScoredResults(attemptKey, result.data);
      resetAttempt();
    } catch (error) {
      // Error handling done by mutation
      // Error is already handled by the mutation hook
    }
  }, [
    flushPendingPersists,
    submittedResults,
    persistedScoredResults,
    attemptKey,
    shouldColdReplay,
    coldReplayFailed,
    canSubmit,
    submitMutation,
    courseId,
    moduleId,
    taskId,
    version,
    resetAttempt,
    recordScoredResults,
  ]);

  // Retry callback
  const retry = useCallback(() => {
    refetch();
  }, [refetch]);

  const retryColdReplay = useCallback(() => {
    coldReplayStartedRef.current = false;
    setColdReplayFailed(false);
    setColdReplayError(null);
  }, []);

  const resetForRetry = useCallback(async () => {
    markRetryCleared(attemptKey);
    resetAttempt();
    clearScoredResults(attemptKey);
    setSubmittedResults(null);
    coldReplayStartedRef.current = false;
    setColdReplayFailed(false);
    setColdReplayError(null);
    await refetch();
  }, [refetch, resetAttempt, clearScoredResults, markRetryCleared, attemptKey]);

  // Determine state
  const state: UseMCQQuizReturn = useMemo(() => {
    // Check for submitted state first
    if (resolvedSubmittedResults) {
      return {
        status: 'submitted',
        results: resolvedSubmittedResults,
        selectAnswer,
        submit,
        canSubmit,
        getAnsweredCount,
        resetForRetry,
      };
    }

    // Check for submitting state
    if (submitMutation.isPending) {
      return {
        status: 'submitting',
        examStatus,
        selectAnswer,
        submit,
        canSubmit,
        getAnsweredCount,
        resetForRetry,
      };
    }

    if (shouldColdReplay) {
      if (coldReplayFailed && coldReplayError) {
        return {
          status: 'cold_replay_failed',
          error: coldReplayError,
          retryColdReplay,
          selectAnswer,
          submit,
          canSubmit,
          getAnsweredCount,
          resetForRetry,
        };
      }
      return {
        status: 'loading',
        selectAnswer,
        submit,
        canSubmit,
        getAnsweredCount,
        resetForRetry,
      };
    }

    // Exam gate states for proctored flow
    if (requiresExamSession) {
      if (examStatus?.closedAt) {
        return {
          status: 'exam_closed',
          examStatus,
          selectAnswer,
          submit,
          canSubmit,
          getAnsweredCount,
          resetForRetry,
        };
      }
      if (!examStatus?.isOpen) {
        return {
          status: 'waiting_for_exam',
          examStatus,
          pollExamStatus,
          selectAnswer,
          submit,
          canSubmit,
          getAnsweredCount,
          resetForRetry,
        };
      }
    }

    // Check for loading state
    if (isLoading || (requiresExamSession && examStatusQuery.isLoading)) {
      return {
        status: 'loading',
        selectAnswer,
        submit,
        canSubmit,
        getAnsweredCount,
        resetForRetry,
      };
    }

    // Check for error states
    if (isError && error) {
      // Check for exam-specific error codes
      type ErrorWithResponse = {
        response?: { status?: number; data?: { detail?: string } };
        message?: string;
        code?: string;
      };
      const errorWithResponse = error as unknown as ErrorWithResponse;
      const errorResponse = errorWithResponse?.response;
      if (errorResponse?.status === 403) {
        const detail = errorResponse?.data?.detail;
        if (detail === 'EXAM_CLOSED') {
          return {
            status: 'exam_closed',
            examStatus,
            selectAnswer,
            submit,
            canSubmit,
            getAnsweredCount,
            resetForRetry,
          };
        }
        if (detail === 'EXAM_NOT_OPEN') {
          return {
            status: 'waiting_for_exam',
            examStatus,
            pollExamStatus,
            selectAnswer,
            submit,
            canSubmit,
            getAnsweredCount,
            resetForRetry,
          };
        }
        if (detail === 'NOT_ADMITTED_FOR_EXAM') {
          return {
            status: 'waiting_for_admission',
            examStatus,
            pollExamStatus,
            selectAnswer,
            submit,
            canSubmit,
            getAnsweredCount,
            resetForRetry,
          };
        }
        if (detail === 'NOT_REGISTERED_FOR_EXAM') {
          return {
            status: 'needs_booking',
            selectAnswer,
            submit,
            canSubmit,
            getAnsweredCount,
            resetForRetry,
          };
        }
      }

      // Generic error
      return {
        status: 'error',
        error: {
          message: errorWithResponse?.message || 'Network request failed',
          code: errorWithResponse?.code,
        },
        retry,
        selectAnswer,
        submit,
        canSubmit,
        getAnsweredCount,
        resetForRetry,
      };
    }

    // Ready state with questions
    if (questionsData?.data) {
      return {
        status: 'ready',
        questions: questionsData.data as MCQQuestion[],
        answers,
        examStatus,
        selectAnswer,
        submit,
        canSubmit,
        getAnsweredCount,
        resetForRetry,
      };
    }

    // Default loading state
    return {
      status: 'loading',
      selectAnswer,
      submit,
      canSubmit,
      getAnsweredCount,
      resetForRetry,
    };
  }, [
    resolvedSubmittedResults,
    submitMutation.isPending,
    shouldColdReplay,
    coldReplayFailed,
    coldReplayError,
    retryColdReplay,
    examStatus,
    examStatusQuery.isLoading,
    requiresExamSession,
    isLoading,
    isError,
    error,
    questionsData,
    answers,
    selectAnswer,
    submit,
    canSubmit,
    getAnsweredCount,
    retry,
    resetForRetry,
    pollExamStatus,
  ]);

  return state;
}
